class WebSocketService {
  private socket: WebSocket | null = null;
  private socketUrl: string = process.env.REACT_APP_SOCKET_URL || ""; 
  private reconnectTimer: NodeJS.Timeout | null = null;
  private lastToken: string | null = null;
  private lastUrl: string | null = null;

  private getBaseSocketUrl(): string {
    if (this.socketUrl && this.socketUrl.trim() !== "") {
      return this.socketUrl;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname === 'localhost' ? 'localhost:8000' : window.location.host;
    return `${protocol}//${host}`;
  }

  connect(token: string, url: string): void {
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        return;
      }
      this.close();
    }

    this.lastToken = token;
    this.lastUrl = url;

    try {
      const baseUrl = this.getBaseSocketUrl();
      const fullUrl = `${baseUrl}${url}?token=${token}`;
      this.socket = new WebSocket(fullUrl);

      this.socket.onopen = () => {
        console.log('WebSocket connected');
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'error') {
            console.error('WebSocket Application Error:', data.message);
          } else if (data.type === 'connection_established') {
            console.log('WebSocket successfully established for user', data.user_id);
          }
        } catch (e) {
          // Regular messages are handled by event listeners in components
        }
      };

      this.socket.onclose = (event) => {
        const reason = event.reason || 'No reason provided';
        console.log(`WebSocket closed: Code ${event.code}, Reason: ${reason}`);
        this.socket = null;

        // Detailed logging for common codes
        if (event.code === 1006) {
          console.error('Abnormal WebSocket closure (1006). This is often a proxy (Nginx) or SSL error.');
        } else if (event.code === 1008) {
          console.error('WebSocket Policy Violation (1008). Check your authentication token.');
        }

        // Don't reconnect if it was a normal closure (e.g. logout)
        if (event.code !== 1000 && event.code !== 1001) {
          this.attemptReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket connection error. Checking network logs might provide more info.');
        if (this.socket) {
          this.socket.close();
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket instance:', err);
      this.attemptReconnect();
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectTimer) return;

    console.log('Attempting to reconnect in 5 seconds...');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.lastToken && this.lastUrl) {
        this.connect(this.lastToken, this.lastUrl);
      }
    }, 5000);
  }

  send(data: object): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getSocket(): WebSocket | null {
    return this.socket;
  }

  close(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
    this.lastToken = null;
    this.lastUrl = null;
  }
}

const websocketService = new WebSocketService();
export default websocketService;